import PropTypes from "prop-types";
import { useMemo } from "react";

// third-party
import { merge } from "lodash";

// material-ui
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// project import
import useConfig from "hooks/useConfig";
import Palette from "./palette";
import Typography from "./typography";
import CustomShadows from "./shadows";
import componentsOverride from "./overrides";

// ==============================|| PERMISSION MANAGER - COMPONENT OVERRIDES ||============================== //
// note: overrides pulled in from PermissionUnitManager's page-local theme so it uses the shared ThemeCustomization theme instead
function permissionComponentsOverride(theme) {
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: "none",
          fontSize: "0.8rem",
          fontWeight: 500,
          letterSpacing: "0.2px",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        contained: {
          background: theme.palette.primary.dark,
          color: "#E1F5EE",
          "&:hover": { background: theme.palette.primary.main },
        },
        outlined: {
          borderColor: "rgba(0,0,0,0.12)",
          color: theme.palette.text.primary,
          "&:hover": {
            borderColor: "rgba(0,0,0,0.24)",
            background: "rgba(0,0,0,0.02)",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          "&+.MuiDialogContent-root": { paddingTop: "16px !important" },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "0.5px solid rgba(0,0,0,0.06)",
          padding: "11px 14px",
          fontSize: "0.78rem",
        },
        head: {
          fontFamily: "'DM Mono', monospace",
          fontSize: "0.65rem",
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          color: "#888780",
          fontWeight: 500,
          background: "#FAFAF8",
          padding: "9px 14px",
          whiteSpace: "nowrap",
          borderBottom: "0.5px solid rgba(0,0,0,0.08)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "rgba(0,0,0,0.1)" },
            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.2)" },
            "&.Mui-focused fieldset": {
              borderColor: "#0F6E56",
              boxShadow: "0 0 0 3px rgba(15,110,86,0.08)",
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: "#FFFFFF",
          border: "0.5px solid rgba(0,0,0,0.1)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          borderRadius: 10,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": { color: theme.palette.primary.main },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
  };
}

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
  const { themeDirection, mode, presetColor, fontFamily } = useConfig();

  const theme = useMemo(() => Palette(mode, presetColor), [mode, presetColor]);

  const themeTypography = useMemo(() => Typography(fontFamily), [fontFamily]);

  const themeCustomShadows = useMemo(() => CustomShadows(theme), [theme]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440,
        },
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      shape: { borderRadius: 6 },
      typography: {
        ...themeTypography,
        h4: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        button: { fontWeight: 500, letterSpacing: "0.2px" },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 500,
              letterSpacing: "0.2px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            },
            contained: {
              background: theme.palette.primary.dark,
              color: "#E1F5EE",
              "&:hover": { background: theme.palette.primary.main },
            },
            outlined: {
              borderColor: "rgba(0,0,0,0.12)",
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: "rgba(0,0,0,0.24)",
                background: "rgba(0,0,0,0.02)",
              },
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              "&+.MuiDialogContent-root": { paddingTop: "16px !important" },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: "0.5px solid rgba(0,0,0,0.06)",
              padding: "11px 14px",
              fontSize: "0.78rem",
            },
            head: {
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.65rem",
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              color: "#888780",
              fontWeight: 500,
              background: "#FAFAF8",
              padding: "9px 14px",
              whiteSpace: "nowrap",
              borderBottom: "0.5px solid rgba(0,0,0,0.08)",
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "rgba(0,0,0,0.1)" },
                "&:hover fieldset": { borderColor: "rgba(0,0,0,0.2)" },
                "&.Mui-focused fieldset": {
                  borderColor: "#0F6E56",
                  boxShadow: "0 0 0 3px rgba(15,110,86,0.08)",
                },
              },
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              background: "#FFFFFF",
              border: "0.5px solid rgba(0,0,0,0.1)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
              borderRadius: 10,
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: {
              "&.Mui-checked": { color: theme.palette.primary.main },
              "&.Mui-checked + .MuiSwitch-track": {
                backgroundColor: theme.palette.primary.main,
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: { fontSize: "0.78rem" },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: { fontSize: "0.78rem" },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: { fontSize: "0.78rem" },
          },
        },
      },
    }),
    [themeDirection, theme, themeTypography, themeCustomShadows],
  );

  const themes = createTheme(themeOptions);
  // themes.components = merge(
  //   componentsOverride(themes),
  //   // permissionComponentsOverride(themes),
  // );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
